import ProjectTaskMonthNavigation from '@/frontend/features/Fde/ProjectTaskMonthNavigation'
import { cn } from '@/lib/utils'
import ProjectTaskCalendarEntry from '@/frontend/features/Fde/ProjectTaskCalendarEntry'
import ProjectTaskCard from '@/frontend/features/Fde/ProjectTaskCard'
import type { TaskSurfaceProps } from '@/frontend/features/Fde/ProjectTaskCanvas'
import ProjectTaskMenu from '@/frontend/features/Fde/ProjectTaskMenu'
import { useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import ProjectCalendarEvent from '@/frontend/features/Fde/ProjectCalendarEvent'
import ProjectCalendarEventDialog from '@/frontend/features/Fde/ProjectCalendarEventDialog'
import useProjectCalendarEvents from '@/frontend/features/Fde/useProjectCalendarEvents'
import { eventMatchesFilters, eventOccursOn } from '@/frontend/features/Fde/projectCalendarEvents'
import type { ProjectTaskAssignee, TimelineEvent } from '@/frontend/features/Fde/types'

interface Props extends TaskSurfaceProps {
  projectId: string
  canViewEvents: boolean
  canViewFiles: boolean
  calendarQuery: string
  calendarPerson?: ProjectTaskAssignee
  offset: number
  onOffset: (value: number) => void
  today: string
}

function dateKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

export default function ProjectTaskSchedule({
  tasks,
  today,
  overdueIds,
  onSelect,
  copy,
  offset,
  onOffset,
  canManage,
  busy,
  onPatch,
  onCopy,
  projectId,
  canViewEvents,
  canViewFiles,
  calendarQuery,
  calendarPerson,
}: Props) {
  const current = new Date(`${today}T12:00:00`)
  const first = new Date(current.getFullYear(), current.getMonth() + offset, 1, 12)
  const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate()
  const leadingDays = (first.getDay() + 6) % 7
  const cells = Math.ceil((leadingDays + daysInMonth) / 7) * 7
  const undated = tasks.filter((task) => !task.due_on)
  const calendar = useProjectCalendarEvents(projectId, dateKey(first), canViewEvents)
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
  const selectedEvent = calendar.events.find((event) => event.id === selectedEventId) ?? null
  const events = calendar.events.filter((event) => eventMatchesFilters(event, calendarQuery, calendarPerson))

  return (
    <div data-testid="project-calendar" className="h-full overflow-auto p-4 lg:p-5">
      <ProjectTaskMonthNavigation
        month={first}
        offset={offset}
        onOffset={onOffset}
        hint={canViewEvents ? copy.management.schedule_hint : copy.management.schedule_tasks_hint}
        copy={copy}
      />
      {calendar.loading && <p role="status" className="mb-3 flex items-center gap-1.5 text-xs text-muted-foreground"><RefreshCw className="size-3 animate-spin" />{copy.timeline.loading_calendar}</p>}
      {calendar.failed && <div role="alert" className="mb-3 flex items-center gap-3 text-xs text-destructive"><p>{copy.timeline.calendar_error}</p><Button size="sm" variant="outline" onClick={calendar.retry}>{copy.management.calendar_retry}</Button></div>}
      <div className="min-w-[42rem] overflow-hidden rounded-xl border border-border">
        <div className="grid grid-cols-7 border-b border-border bg-muted/50">
          {copy.management.weekdays.map((day) => (
            <div key={day} className="px-3 py-2 text-xs text-muted-foreground">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {Array.from({ length: cells }, (_, index) => {
            const date = new Date(first.getFullYear(), first.getMonth(), index - leadingDays + 1, 12)
            const key = dateKey(date)
            const inMonth = date.getMonth() === first.getMonth()
            const dayTasks = tasks.filter((task) => task.due_on === key)
            return (
              <div
                key={key}
                data-date={key}
                className={cn(
                  'min-h-28 border-b border-r border-border/60 p-2',
                  !inMonth && 'bg-muted/30',
                  key === today && 'bg-primary/5',
                )}
              >
                <time
                  dateTime={key}
                  className={cn(
                    'mb-2 inline-flex size-6 items-center justify-center rounded-full text-[11px]',
                    key === today
                      ? 'bg-primary font-semibold text-primary-foreground'
                      : inMonth
                        ? 'text-foreground'
                        : 'text-muted-foreground/50',
                  )}
                >
                  {date.getDate()}
                </time>
                <div className="max-h-40 space-y-1 overflow-y-auto">
                  {events.filter((event) => eventOccursOn(event, key)).map((event: TimelineEvent) => (
                    <ProjectCalendarEvent key={event.id} event={event} copy={copy} onSelect={() => setSelectedEventId(event.id)} />
                  ))}
                  {dayTasks.map((task) => (
                    <ProjectTaskCalendarEntry
                      key={task.id}
                      task={task}
                      canManage={canManage}
                      busy={busy}
                      onSelect={() => onSelect(task.id)}
                      onPatch={onPatch}
                      onCopy={onCopy}
                      copy={copy}
                      overdue={overdueIds.has(task.id)}
                    />
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>
      {undated.length > 0 && (
        <section className="mt-5">
          <h3 className="mb-3 text-xs font-semibold">
            {copy.management.no_due_date} <span className="ml-1 text-muted-foreground">{undated.length}</span>
          </h3>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {undated.map((task) => (
              <ProjectTaskMenu
                key={task.id}
                task={task}
                canManage={canManage}
                busy={busy}
                onSelect={() => onSelect(task.id)}
                onPatch={onPatch}
                onCopy={onCopy}
                copy={copy}
              >
                <div>
                  <ProjectTaskCard
                    task={task}
                    overdue={overdueIds.has(task.id)}
                    onSelect={() => onSelect(task.id)}
                    copy={copy}
                  />
                </div>
              </ProjectTaskMenu>
            ))}
          </div>
        </section>
      )}
      <ProjectCalendarEventDialog event={selectedEvent} onClose={() => setSelectedEventId(null)} projectId={projectId} canViewFiles={canViewFiles} copy={copy} />
    </div>
  )
}
